import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rsvpService, eventsService } from '../services/api-service';
import { MapPin, Clock, Send, Settings, Users, CheckCircle, AlertCircle } from 'lucide-react';
import './LocationRevealAdmin.css';

const LocationRevealAdmin = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [revealStatus, setRevealStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    notificationOrder: 'furthest-first', // 'furthest-first' or 'closest-first'
    bufferTime: 20, // minutes
    eventLocation: '',
    eventDate: '',
    eventTime: ''
  });
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    if (eventId) {
      loadData();
    }
  }, [eventId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventData, rsvpsData, statusData] = await Promise.all([
        eventsService.getEventById(eventId),
        rsvpService.getRSVPsByEvent(eventId),
        rsvpService.getLocationRevealStatus(eventId)
      ]);

      if (eventData) {
        setEvent(eventData);
        setSettings(prev => ({
          ...prev,
          eventLocation: eventData.location || '',
          eventDate: eventData.date || '',
          eventTime: eventData.time?.start || ''
        }));
      }

      setRsvps(rsvpsData || []);
      setRevealStatus(statusData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerReveal = async () => {
    if (!settings.eventLocation) {
      alert('Please set the event location first');
      return;
    }

    if (!window.confirm('Are you sure you want to trigger location reveals for all RSVPs? This will send the location to everyone.')) {
      return;
    }

    setTriggering(true);
    try {
      await rsvpService.triggerLocationReveal(eventId, {
        notificationOrder: settings.notificationOrder,
        bufferTime: settings.bufferTime,
        eventLocation: settings.eventLocation,
        eventDate: settings.eventDate,
        eventTime: settings.eventTime
      });
      alert('Location reveals triggered successfully!');
      await loadData(); // Reload to see updated status
    } catch (error) {
      console.error('Error triggering location reveal:', error);
      alert('Failed to trigger location reveals. Please try again.');
    } finally {
      setTriggering(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Not set';
    return timeString;
  };

  const calculateRevealTime = (travelTimeMinutes, eventDateTime) => {
    if (!eventDateTime || !travelTimeMinutes) return 'Calculating...';
    
    const eventDate = new Date(eventDateTime);
    const bufferMs = (settings.bufferTime + travelTimeMinutes) * 60 * 1000;
    const revealTime = new Date(eventDate.getTime() - bufferMs);
    
    return revealTime.toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="location-reveal-admin-page">
        <div className="container">
          <div className="location-reveal-admin-login">
            <h2>Login Required</h2>
            <p>Please log in to access location reveal admin.</p>
            <button onClick={() => navigate('/?login=true')} className="btn">
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="location-reveal-admin-page">
        <div className="container">
          <div className="location-reveal-admin-loading">
            <Settings size={64} className="spinning" />
            <h3>Loading...</h3>
          </div>
        </div>
      </div>
    );
  }

  // Sort RSVPs by travel time based on notification order
  const sortedRsvps = [...rsvps].sort((a, b) => {
    const aTime = a.travelTimeMinutes || 0;
    const bTime = b.travelTimeMinutes || 0;
    
    if (settings.notificationOrder === 'furthest-first') {
      return bTime - aTime; // Furthest first
    } else {
      return aTime - bTime; // Closest first
    }
  });

  return (
    <div className="location-reveal-admin-page">
      {/* Header */}
      <section className="location-reveal-admin-header">
        <div className="container">
          <div className="location-reveal-admin-header-content">
            <button onClick={() => navigate('/events')} className="location-reveal-admin-back">
              ← Back to Events
            </button>
            <div className="location-reveal-admin-header-main">
              <MapPin size={48} className="location-reveal-admin-icon" />
              <div>
                <h1>Location Reveal Admin</h1>
                <p>{event?.title || 'Event Management'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Settings */}
      <section className="location-reveal-admin-settings section">
        <div className="container">
          <div className="location-reveal-admin-card">
            <h2>
              <Settings size={24} />
              Configuration
            </h2>
            
            <div className="location-reveal-admin-form">
              <div className="location-reveal-admin-form-group">
                <label htmlFor="event-location">Event Location *</label>
                <input
                  type="text"
                  id="event-location"
                  value={settings.eventLocation}
                  onChange={(e) => setSettings({ ...settings, eventLocation: e.target.value })}
                  placeholder="e.g., Eleanor Shipley Park, LOT 135 Seaview Terrace, Moffat Beach"
                  required
                />
                <small>The exact location that will be revealed to attendees</small>
              </div>

              <div className="location-reveal-admin-form-row">
                <div className="location-reveal-admin-form-group">
                  <label htmlFor="event-date">Event Date *</label>
                  <input
                    type="date"
                    id="event-date"
                    value={settings.eventDate ? new Date(settings.eventDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setSettings({ ...settings, eventDate: e.target.value })}
                    required
                  />
                </div>

                <div className="location-reveal-admin-form-group">
                  <label htmlFor="event-time">Event Start Time *</label>
                  <input
                    type="time"
                    id="event-time"
                    value={settings.eventTime}
                    onChange={(e) => setSettings({ ...settings, eventTime: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="location-reveal-admin-form-group">
                <label htmlFor="notification-order">Notification Order</label>
                <select
                  id="notification-order"
                  value={settings.notificationOrder}
                  onChange={(e) => setSettings({ ...settings, notificationOrder: e.target.value })}
                >
                  <option value="furthest-first">Furthest First (Default)</option>
                  <option value="closest-first">Closest First</option>
                </select>
                <small>
                  {settings.notificationOrder === 'furthest-first' 
                    ? 'Furthest people get location earliest, closest get it latest'
                    : 'Closest people get location earliest, furthest get it latest'}
                </small>
              </div>

              <div className="location-reveal-admin-form-group">
                <label htmlFor="buffer-time">Buffer Time (minutes)</label>
                <input
                  type="number"
                  id="buffer-time"
                  value={settings.bufferTime}
                  onChange={(e) => setSettings({ ...settings, bufferTime: parseInt(e.target.value) || 20 })}
                  min="0"
                  max="60"
                />
                <small>Extra time added for traffic, parking, etc. (Recommended: 15-20 minutes)</small>
              </div>

              <button
                onClick={handleTriggerReveal}
                className="btn btn-orange btn-large"
                disabled={triggering || !settings.eventLocation || !settings.eventDate || !settings.eventTime}
              >
                {triggering ? (
                  'Triggering...'
                ) : (
                  <>
                    <Send size={20} />
                    Trigger Location Reveals
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* RSVPs List */}
      <section className="location-reveal-admin-rsvps section">
        <div className="container">
          <div className="location-reveal-admin-card">
            <div className="location-reveal-admin-card-header">
              <h2>
                <Users size={24} />
                RSVPs ({rsvps.length})
              </h2>
              {revealStatus && (
                <div className="location-reveal-admin-status">
                  <span className={`status-badge ${revealStatus.allSent ? 'success' : 'pending'}`}>
                    {revealStatus.allSent ? (
                      <>
                        <CheckCircle size={16} />
                        All Sent
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} />
                        {revealStatus.sentCount || 0} / {revealStatus.totalCount || 0} Sent
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>

            {rsvps.length === 0 ? (
              <div className="location-reveal-admin-empty">
                <Users size={48} />
                <p>No RSVPs yet for this event</p>
              </div>
            ) : (
              <div className="location-reveal-admin-rsvps-list">
                {sortedRsvps.map((rsvp) => (
                  <div key={rsvp.id} className="location-reveal-admin-rsvp-item">
                    <div className="location-reveal-admin-rsvp-header">
                      <div>
                        <h3>{rsvp.name}</h3>
                        <p className="location-reveal-admin-rsvp-phone">{rsvp.phone}</p>
                      </div>
                      {rsvp.locationRevealed ? (
                        <span className="status-badge success">
                          <CheckCircle size={16} />
                          Revealed
                        </span>
                      ) : (
                        <span className="status-badge pending">Pending</span>
                      )}
                    </div>
                    
                    <div className="location-reveal-admin-rsvp-details">
                      <div className="location-reveal-admin-rsvp-detail">
                        <MapPin size={16} />
                        <span><strong>From:</strong> {rsvp.startingLocation || 'Not provided'}</span>
                      </div>
                      {rsvp.travelTimeMinutes && (
                        <div className="location-reveal-admin-rsvp-detail">
                          <Clock size={16} />
                          <span><strong>Travel Time:</strong> {rsvp.travelTimeMinutes} minutes</span>
                        </div>
                      )}
                      {rsvp.revealScheduledAt && (
                        <div className="location-reveal-admin-rsvp-detail">
                          <Send size={16} />
                          <span><strong>Scheduled:</strong> {new Date(rsvp.revealScheduledAt).toLocaleString('en-AU')}</span>
                        </div>
                      )}
                      {rsvp.locationRevealedAt && (
                        <div className="location-reveal-admin-rsvp-detail">
                          <CheckCircle size={16} />
                          <span><strong>Revealed:</strong> {new Date(rsvp.locationRevealedAt).toLocaleString('en-AU')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationRevealAdmin;


